<template>
  <!-- Otevření dialogu -->
  <v-btn
    text
    color="deep-purple-lighten-1"
    class="pa-3 my-1 text-center"
    width="100%"
    height="100%"
    style="line-height: 25px;"
    @click="dialog = true"
  >
    {{ $t('event.absences.absence_button') }}
  </v-btn>

  <v-dialog v-model="dialog" max-width="800px">
    <v-card>
      <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>

      <v-card-title>{{ $t('event.absences.title') }}</v-card-title>
      <v-card-text>
        <!-- Existující omluvenky -->
        <v-skeleton-loader
          v-if="loadingAbsences"
          class="mx-auto"
          type="article"
        ></v-skeleton-loader>
        <div v-if="existingAbsences.length">
          <h3 class="subtitle-1 font-weight-medium">{{ $t('event.absences.existing_absences') }}</h3>
          <v-list>
            <v-list-item
              v-for="abs in existingAbsences"
              :key="abs.id"
              class="pa-0"
            >
              <v-row class="align-center">
                <v-col cols="12" md="4" class="d-flex align-center justify-space-between min-w-0">
                  <span class="text-truncate">{{ abs.first_name }} {{ abs.last_name }} ({{ abs.index }})</span>
                  <!-- Delete jen na malých -->
                        <v-icon color="red" class="d-inline-flex d-md-none ml-2" @click="prepareDelete(abs)">mdi-delete</v-icon>
                </v-col>

                <!-- Poznámka -->
                <v-col cols="12" md="7" class="min-w-0">
                  <v-text-field
                    v-model="abs.note"
                    density="compact"
                    :placeholder="$t('event.absences.reason_placeholder')"
                    class="w-100"
                    counter
                    maxlength="150"
                  />
                </v-col>

                <!-- Delete jen na velkých -->
                <v-col cols="12" md="1" class="d-none d-md-flex justify-end">
                  <v-icon color="red" @click="prepareDelete(abs)">mdi-delete</v-icon>
                </v-col>
              </v-row>

            </v-list-item>
          </v-list>
          <v-card-actions>
            <v-spacer />
            <v-btn color="green" :loading="saving" @click="saveAll">{{ $t('general.save_changes') }}</v-btn>
          </v-card-actions>
        </div>

        <!-- Nové omluvenky -->
        <div v-if="availableAbsences.length">
          <h3 class="subtitle-1 font-weight-medium">{{ $t('event.absences.add_title') }}</h3>
          <v-list>
 <v-list-item
  v-for="opt in availableAbsences"
  :key="opt.role + '-' + opt.race"
>
  <div
    class="d-flex align-center w-100"
    style="flex-wrap: nowrap; overflow-x: auto; gap: 8px;"
  >
    <!-- Jméno (nechat zkrátit, ale držet minimální šířku) -->
    <div
      class="text-truncate"
      style="flex: 0 0 auto;"
    >
      {{ opt.first_name }} {{ opt.last_name }} ({{ opt.index }})
    </div>

    <!-- Poznámka -->
    <v-text-field
      v-model="opt.note"
      density="compact"
      :placeholder="$t('event.absences.reason_placeholder')"
      counter
      maxlength="150"
      style="min-width: 260px;"
    />

    <!-- Odeslat -->
    <v-btn
      icon
      color="green"
      variant="text"
      style="flex: 0 0 auto;"
      @click="createAbsence(opt)"
      aria-label="Odeslat"
    >
      <v-icon size="40">mdi-send-circle</v-icon>
    </v-btn>
  </div>
</v-list-item>


          </v-list>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="dialog = false">{{ $t('general.close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Potvrzovací dialog pro smazání -->
  <v-dialog v-if="deleteTargetId" v-model="confirmDialog" max-width="600px">
    <v-card>
      <v-card-title class="headline">{{ $t('event.absences.delete_confirm_title') }}</v-card-title>
      <v-card-text>
        {{ $t('event.absences.delete_confirm_text') }} <b>{{ deleteTargetId?.first_name }} {{ deleteTargetId?.last_name }}</b>?
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="cancelDelete">{{ $t('general.cancel') }}</v-btn>
        <v-btn color="red" @click="confirmDelete">{{ $t('general.delete') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>




</template>


<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import apiClient from '/api.js'
import {useSnackbarStore} from '@/stores/snackbarStore'
import {useI18n} from 'vue-i18n'


const { t } = useI18n()
const dialog = ref(false)
const existingAbsences = ref([])
const availableAbsences = ref([])
const saving = ref(false)
const route = useRoute()
const eventId = route.params.id
const loadingAbsences = ref(false)
const confirmDialog = ref(false)
const deleteTargetId = ref(null)
const snackbar = useSnackbarStore()
const errorMessage = ref('')

// Načtení dat
const fetchAbsences = async () => {
  loadingAbsences.value = true
  try {
    const res = await apiClient.get(`/event/${eventId}/absences/?mode=personal`)
    existingAbsences.value = res.data.edit
    availableAbsences.value = res.data.create
  } catch (e) {
    console.error('Chyba při načítání omluvenek', e)
  } finally {
    loadingAbsences.value = false
  }
}

// Načti při otevření dialogu
watch(dialog, open => {
  if (open) fetchAbsences()
})

// Smazání

// Prepare delete
function prepareDelete(abs) {
  deleteTargetId.value = abs
  confirmDialog.value = true
}

// Cancel delete
function cancelDelete() {
  confirmDialog.value = false
  deleteTargetId.value = null
}

async function confirmDelete() {
  await deleteAbsence(deleteTargetId.value.id)
  confirmDialog.value = false
  deleteTargetId.value = null
}

const deleteAbsence = async id => {
  try {
    await apiClient.delete(`/event/${eventId}/absences/?absence_id=${id}`)
    confirmDialog.value = false
    deleteTargetId.value = null
    existingAbsences.value = ""
    availableAbsences.value = ""
    await fetchAbsences()
    snackbar.success(t('event.absences.delete'))

  } catch (e) {
    errorMessage.value = t('event.absences.delete_error')
    console.error('Chyba při mazání omluvenky', e)
  }
}

// Vytvoření
const createAbsence = async opt => {
  try {
    const { ...payload } = opt
    payload.event = eventId
    await apiClient.post(`/event/${eventId}/absences/?mode=personal`, payload)
    existingAbsences.value = ""
    availableAbsences.value = ""

    await fetchAbsences()
    snackbar.success(t('event.absences.create'))

  } catch (e) {
    console.error('Chyba při vytváření omluvenky', e)
    if (e.response && e.response.status === 409) {
      // použijeme zprávu z backendu
      errorMessage.value = e.response.data.detail
    } else {
      errorMessage.value = t('event.absences.create_error')
    }
  }
}


// Ulož všechny stávající poznámky metodou PUT
const saveAll = async () => {
  try {
    saving.value = true
    const payload = existingAbsences.value.map(({ id, note }) => ({ id, note }))
    await apiClient.put(`/event/${eventId}/absences/`, payload)
    existingAbsences.value = ""
    availableAbsences.value = ""
    await fetchAbsences()
    snackbar.success(t('event.absences.update_all'))

  } catch (e) {
    console.error('Chyba při ukládání omluvenek', e)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.subtitle-1 {
  margin-bottom: 0.5rem;
}

.min-w-0 { min-width: 0; }
</style>
